import { useEffect, useRef } from 'react';

import { useAuth } from '../auth/use-auth';
import { useScopeStore } from '../store/scope-store';
import { identifyUser } from './posthog';

/**
 * Ties events to whoever is signed in, once.
 *
 * **Mounted at the App level and nowhere else.** `useAuth` is a `useQuery` that
 * any number of components call — putting the identify inside it would fire on
 * every mount of every one of them. Here it happens once per session, and the
 * ref guard makes a re-render or a refetch of the same user a no-op rather
 * than a repeated call.
 *
 * Signing *out* is not handled here: `resetUser` belongs to the action, not to
 * an effect watching for absence. An effect that resets whenever `user` goes
 * null would also fire during the moment before the first session response
 * lands, wiping the identity of someone who is still signed in.
 */
export function useIdentify(): void {
  const { user } = useAuth();
  const teamId = useScopeStore((state) => state.selectedTeamId);
  const identified = useRef<string | null>(null);

  useEffect(() => {
    if (!user || identified.current === user.uid) return;

    identified.current = user.uid;
    identifyUser(user.uid, {
      // The person is the identity; the account they are acting as is a
      // property, so team activity stays separable without becoming a
      // different user.
      ...(teamId ? { team_id: teamId } : {}),
      ...(user.email ? { email: user.email } : {}),
      ...(user.displayName ? { name: user.displayName } : {}),
    });
  }, [teamId, user]);
}
